import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import postal from 'postal';
import ErrorBoundary from '../../../shared/error-boundary';
import Loading from '../../../shared/loading';
import defaultOnErrorHandler from '../../../shared/default-on-error-handler';
import {
  SortableFilterableTableHeader,
  SORT_DIRECTION,
} from '../../../shared/sortable-filterable-table-header';
import EditCategoryModal from '../categories/edit-category-modal';
import { getById } from '../../../services/lwadmin/category/category-service';

const dirs = {
  name: 'name_changes_updated_atSortDirection',
  wire: 'wires_assigned_updated_atSortDirection',
};

const arrs = {
  name: 'category_name_changes',
  wire: 'wire_category_reference_changes',
};

const nameChanges = source => ({
  ...(source || {
    new_name: '',
    by_user: '',
  }),
  updated_at: _.get(source, 'updated_at') ? moment(source.updated_at) : null,
});

const wireReferenceChanges = source => ({
  ...(source || {
    wire: { id: null, title: '' },
    by_user: '',
  }),
  updated_at: _.get(source, 'updated_at') ? moment(source.updated_at) : null,
});

const createCategory = source => ({
  ...(source || {
    id: null,
    name: '',
    description: '',
    slug: '',
    stats: { wires_count: 0 },
  }),
  [arrs.name]: _.get(source, arrs.name, []).map(nameChanges),
  [arrs.wire]: _.get(source, arrs.wire, []).map(wireReferenceChanges),
});

const DataTable = ({ type, onSort, data }) => (
  <section
    data-role="tab-panel"
    className="active"
    id={{ wire: 'assigned_wires', name: 'category_name_changes' }[type]}
  >
    <h3>{{ wire: 'Assigned Wires', name: 'Category Name Changes' }[type]}</h3>
    <table className="table table-hover">
      <thead>
        <tr>
          <SortableFilterableTableHeader onSort={onSort}>
            Updated at
          </SortableFilterableTableHeader>
          <th>Title</th>
          <th>By</th>
        </tr>
      </thead>
      <tbody>
        {data.slice(0, 30).map(d => (
          <tr key={Math.random()}>
            <td>{d.updated_at.format('LLLL')}</td>
            <td>{_.get(d, { wire: 'wire.title', name: 'new_name' }[type])}</td>
            <td>{d.by_user}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </section>
);

DataTable.propTypes = {
  type: PropTypes.oneOf(['wire', 'name']).isRequired,
  onSort: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({
    updated_at: PropTypes.objectOf(PropTypes.object),
    by_user: PropTypes.string,
  })).isRequired,
};

export default class CategoryAdmin extends React.Component {
  static propTypes = {
    categoryId: PropTypes.number.isRequired,
  }

  constructor(props, context) {
    super(props, context);

    this.state = {
      category: createCategory(),
      [dirs.wire]: SORT_DIRECTION.unsorted,
      [dirs.name]: SORT_DIRECTION.unsorted,
    };
  }

  componentDidMount() {
    postal.subscribe({
      channel: 'lwadmin.category.commands',
      topic: 'edit-category',
      callback: () => this.editCategory(),
    });
    this.refresh();
  }

  editCategory() {
    this.editCategoryModal.present(this.state.category.id);
  }

  refresh() {
    this.loading.start();
    getById(this.props.categoryId)
      .then(result => this.setState({ category: createCategory(result.data.category) }))
      .catch(error => defaultOnErrorHandler(error, 'Error loading category'))
      .finally(() => this.loading.stop());
  }

  render() {
    return (
      <ErrorBoundary>
        <div className="lwadmin-category-admin">
          <Loading ref={c => (this.loading = c)}>
            <h1>{`Category "${this.state.category.name}"`}</h1>
            <p>
              <strong>Description:</strong>
              {` ${this.state.category.description}`}
              <br />
              <strong>Wires count:</strong>
              {` ${this.state.category.stats.wires_count}`}
              <br />
            </p>
            <section data-role="tab-set">
              {['name', 'wire'].map(type => (
                <DataTable
                  key={Math.random()}
                  type={type}
                  onSort={dir => this.setState({ [dirs[type]]: dir })}
                  data={this.state.category[arrs[type]]}
                />
              ))}
            </section>
          </Loading>
          <EditCategoryModal
            ref={c => (this.editCategoryModal = c)}
            categoryDidSave={() => this.refresh()}
          />
        </div>
      </ErrorBoundary>
    );
  }
}
